import React from 'react';
import { useToast } from '@/components/ui/use-toast';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { ExternalLink, Link, GitBranch, Copy } from 'lucide-react';

export function TaskContextMenu({ task, organizationSlug, children }) {
  const { toast } = useToast();

  const taskUrl = `/${organizationSlug}/plan/${task.key}`;
  const fullUrl = `${window.location.origin}${taskUrl}`;

  const generateBranchName = (task) => {
    const key = task.key; // Keep key in original case (uppercase)
    const summary = task.summary
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    return `${key}-${summary}`;
  };

  const handleOpenInNewTab = () => {
    window.open(taskUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast({
        description: "Task link has been copied to clipboard",
      });
    } catch (err) {
      toast({
        description: "Unable to copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleCopyBranchName = async () => {
    const branchName = generateBranchName(task);
    try {
      await navigator.clipboard.writeText(branchName);
      toast({
        description: `"${branchName}" has been copied to clipboard`,
      });
    } catch (err) {
      toast({
        description: "Unable to copy branch name to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleCopyShortLink = async () => {
    const shortLink = `<a href="${fullUrl}">${task.key}</a>`;
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([shortLink], { type: 'text/html' }),
          'text/plain': new Blob([`${task.key} - ${fullUrl}`], { type: 'text/plain' })
        })
      ]);
      toast({
        description: "Formatted short link has been copied to clipboard",
      });
    } catch (err) {
      // Fallback to plain text if HTML clipboard fails
      try {
        await navigator.clipboard.writeText(`${task.key} - ${fullUrl}`);
        toast({
          description: "Formatted short link has been copied to clipboard",
        });
      } catch (fallbackErr) {
        toast({
          description: "Unable to copy short link to clipboard",
          variant: "destructive",
        });
      }
    }
  };

  const handleCopyLongLink = async () => {
    const longLink = `<a href="${fullUrl}">${task.key}: ${task.summary}</a>`;
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([longLink], { type: 'text/html' }),
          'text/plain': new Blob([`${task.key}: ${task.summary} - ${fullUrl}`], { type: 'text/plain' })
        })
      ]);
      toast({
        description: "Formatted long link has been copied to clipboard",
      });
    } catch (err) {
      // Fallback to plain text if HTML clipboard fails
      try {
        await navigator.clipboard.writeText(`${task.key}: ${task.summary} - ${fullUrl}`);
        toast({
          description: "Formatted long link has been copied to clipboard",
        });
      } catch (fallbackErr) {
        toast({
          description: "Unable to copy long link to clipboard",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem onClick={handleOpenInNewTab} className="cursor-pointer">
          <ExternalLink className="mr-2 h-4 w-4" />
          Open in new tab
        </ContextMenuItem>
        <ContextMenuItem onClick={handleCopyLink} className="cursor-pointer">
          <Link className="mr-2 h-4 w-4" />
          Copy URL
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleCopyShortLink} className="cursor-pointer">
          <Copy className="mr-2 h-4 w-4" />
          Copy key link
        </ContextMenuItem>
        <ContextMenuItem onClick={handleCopyLongLink} className="cursor-pointer">
          <Copy className="mr-2 h-4 w-4" />
          Copy summary link
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleCopyBranchName} className="cursor-pointer">
          <GitBranch className="mr-2 h-4 w-4" />
          Copy branch name
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
